import React, { useCallback, useState } from "react";
import { Controller } from "react-hook-form";
import {
  Typography,
  TextField,
  Box,
  Stack,
} from "@mui/material";
import debounce from "lodash/debounce";
import Autocomplete from "@mui/material/Autocomplete";

const parseAirportString = (airportStr) => {
  try {
    const parts = airportStr.split(",").map((part) => part.trim());
    const location = parts[0];
    const airportPart = parts[1];
    const country = parts[parts.length - 1];
    const codeMatch = airportPart.match(/\[(.*?)\]/);
    const code = codeMatch ? codeMatch[1] : "";
    const airportName = airportPart.replace(/\s*\[.*?\]/, "").trim();

    return {
      location,
      airportName,
      code,
      country,
      fullString: airportStr,
      displayString: `${location}, ${country}`,
    };
  } catch (error) {
    console.error("Error parsing airport string:", error);
    return {
      location: airportStr,
      airportName: "",
      code: "",
      country: "",
      fullString: airportStr,
      displayString: airportStr,
    };
  }
};

const DepartureForm = ({ control, setValue, errors }) => {
  const initialFromAirport = {
    location: "London",
    airportName: "All Airports",
    code: "LON",
    country: "United Kingdom",
    fullString: "London, All Airports [LON], United Kingdom",
    displayString: "London, United Kingdom",
  };

  const initialToAirport = {
    location: "Dubai",
    airportName: "Dubai Intl Arpt",
    code: "DXB",
    country: "United Arab Emirates",
    fullString: "Dubai, Dubai Intl Arpt [DXB], United Arab Emirates",
    displayString: "Dubai, United Arab Emirates",
  };

  const [fromOptions, setFromOptions] = useState([initialFromAirport]);
  const [toOptions, setToOptions] = useState([initialToAirport]);
  const [fromLoading, setFromLoading] = useState(false);
  const [toLoading, setToLoading] = useState(false);
  const [fromInputValue, setFromInputValue] = useState(
    initialFromAirport.location
  );
  const [toInputValue, setToInputValue] = useState(initialToAirport.location);
  const [fromCache, setFromCache] = useState({});
  const [toCache, setToCache] = useState({});
  const [fromApiError, setFromApiError] = useState(null);
  const [toApiError, setToApiError] = useState(null);

  const [selectedOrigin, setSelectedOrigin] = useState(initialFromAirport);
  const [selectedDestination, setSelectedDestination] =
    useState(initialToAirport);

  React.useEffect(() => {
    setValue("origin", initialFromAirport.code);
    setValue("destination", initialToAirport.code);
  }, [setValue]);

  const fetchAirports = useCallback(
    debounce(
      async (search, setOptions, setLoading, setCache, setApiError, cache) => {
        if (!search || search.length < 2) {
          setOptions([]);
          setApiError(null);
          return;
        }

        if (cache[search]) {
          setOptions(cache[search]);
          return;
        }

        setLoading(true);
        setApiError(null);

        try {
          const response = await fetch(
            `/travelservice/travelservice/completion/${search}`
          );
          if (!response.ok) throw new Error("Failed to fetch airports");

          const data = await response.json();
          const parsedData = data.map(parseAirportString);
          setCache((prev) => ({ ...prev, [search]: parsedData }));
          setOptions(parsedData);
        } catch (errors) {
          setApiError("Unable to fetch results. Please try again.");
          setOptions([]);
        } finally {
          setLoading(false);
        }
      },
      300
    ),
    []
  );

  const renderAirportOption = (option) => {
    if (!option) return "";
    return `${option.location} ${option.code ? `(${option.code})` : ""} - ${
      option.airportName
    }, ${option.country}`;
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ width: "100%" }}
      >
        <Box flex={1}>
          <Controller
            name="origin"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                openOnFocus
                value={selectedOrigin}
                onChange={(_, newValue) => {
                  setSelectedOrigin(newValue);
                  setValue("origin", newValue?.code || "");
                }}
                inputValue={fromInputValue}
                onInputChange={(_, newValue) => {
                  setFromInputValue(newValue);
                  fetchAirports(
                    newValue,
                    setFromOptions,
                    setFromLoading,
                    setFromCache,
                    setFromApiError,
                    fromCache
                  );
                }}
                options={fromOptions}
                getOptionLabel={renderAirportOption}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.code === value.code;
                }}
                loading={fromLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="From"
                    error={!!fromApiError || !!errors?.origin}
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& input": {
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          padding: "8px 14px",
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props;
                  return (
                    <li key={option.code} {...restProps}>
                      <Box sx={{ width: "100%", overflow: "hidden" }}>
                        <Typography noWrap>
                          {option.location} ({option.code})
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          {option.airportName}, {option.country}
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
                groupBy={(option) => option.country}
                sx={{
                  width: "100%",
                  "& .MuiAutocomplete-input": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            )}
          />
        </Box>

        <Box flex={1}>
          <Controller
            name="destination"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                openOnFocus
                value={selectedDestination}
                onChange={(_, newValue) => {
                  setSelectedDestination(newValue);
                  setValue("destination", newValue?.code || "");
                }}
                inputValue={toInputValue}
                onInputChange={(_, newValue) => {
                  setToInputValue(newValue);
                  fetchAirports(
                    newValue,
                    setToOptions,
                    setToLoading,
                    setToCache,
                    setToApiError,
                    toCache
                  );
                }}
                options={toOptions}
                getOptionLabel={renderAirportOption}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.code === value.code;
                }}
                loading={toLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="To"
                    error={!!toApiError || !!errors?.destination}
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& input": {
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          padding: "8px 14px",
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props;
                  return (
                    <li key={option.code} {...restProps}>
                      <Box sx={{ width: "100%", overflow: "hidden" }}>
                        <Typography noWrap>
                          {option.location} ({option.code})
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          {option.airportName}, {option.country}
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
                groupBy={(option) => option.country}
                sx={{
                  width: "100%",
                  "& .MuiAutocomplete-input": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            )}
          />
        </Box>
      </Stack>
    </Box>
  );
};


export default DepartureForm;